import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FlaskConical, Quote } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { WHY_PHILOSOPHY, WHY_PRODUCTS, WHY_CLOSING } from "@/data/whyFemora";

const WhyFemora = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main data-testid="why-femora-page" className="pt-[110px]">
      {/* Hero */}
      <section className="py-16 lg:py-24" style={{ background: "linear-gradient(160deg,#FBF1F3 0%,#FDFBF7 50%,#F3EEF8 100%)" }}>
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-rose bg-white border border-rose/20 rounded-full px-3 py-1.5">
            <FlaskConical size={14} /> Formulation Rationale
          </span>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight text-ink leading-[1.02]">
            Why Femora Life?
          </h1>
          <p className="mt-4 text-xl text-gray-500 font-medium">Why we dose the way we do.</p>
        </div>
      </section>

      {/* Philosophy */}
      <section className="bg-white py-16 lg:py-24 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-widest text-rose mb-3 inline-block border-l-2 border-gold pl-3">Our formulation philosophy</p>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-ink mb-10">Built dose-first, not label-first.</h2>
          </Reveal>
          <div className="space-y-8">
            {WHY_PHILOSOPHY.map((p, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="border-l-2 border-rose/40 pl-6">
                  <h3 className="text-xl font-bold text-ink mb-2">{p.heading}</h3>
                  <p className="text-gray-600 leading-relaxed text-lg">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Per-product rationale */}
      <section className="py-16 lg:py-24" style={{ background: "linear-gradient(180deg,#FBFBFC 0%,#F6F3F8 100%)" }}>
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <Reveal className="mb-12 max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-rose mb-3 inline-block border-l-2 border-gold pl-3">Product by product</p>
            <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tight text-ink">The reasoning behind every formula</h2>
          </Reveal>

          <div className="space-y-5">
            {WHY_PRODUCTS.map((p, i) => (
              <Reveal key={i} delay={Math.min(i * 0.03, 0.25)}>
                <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8" data-testid={`why-product-${p.slug || "omega-3"}`}>
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="h-9 w-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0" style={{ backgroundColor: p.accent }}>
                      {String(i + 1).padStart(2, "0").slice(-2)}
                    </span>
                    <h3 className="font-display text-3xl font-medium tracking-tight text-ink">{p.title}</h3>
                    <span className="text-sm font-medium" style={{ color: p.accent }}>{p.concern}</span>
                  </div>
                  <p className="text-gray-600 leading-relaxed text-[1.05rem]">{p.body}</p>
                  {p.note && (
                    <div className="mt-5 flex items-start gap-3 rounded-lg p-4" style={{ backgroundColor: `${p.accent}12` }}>
                      <Quote size={16} className="shrink-0 mt-1" style={{ color: p.accent }} />
                      <p className="text-sm text-ink/80 italic leading-relaxed">{p.note}</p>
                    </div>
                  )}
                  {p.slug && (
                    <Link to={`/product/${p.slug}`} className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-ink hover:text-rose transition-colors duration-200">
                      View {p.title} <ArrowRight size={14} />
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="bg-ink text-white py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <Reveal>
            <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tight mb-5">{WHY_CLOSING.heading}</h2>
            <p className="text-white/70 text-lg leading-relaxed">{WHY_CLOSING.body}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/products" className="inline-flex items-center gap-2 rounded-md bg-white text-ink font-semibold px-7 py-3.5 hover:bg-rose hover:text-white transition-colors duration-200">
                Shop all products <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="inline-flex items-center rounded-md border border-white/30 text-white font-semibold px-7 py-3.5 hover:border-white transition-colors duration-200">
                Ask us a question
              </Link>
            </div>
            <p className="mt-10 pt-8 border-t border-white/10 text-xs text-white/40 leading-relaxed">{WHY_CLOSING.disclaimer}</p>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default WhyFemora;
